import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { auth, createUserWithEmailAndPassword, sendSignInLinkToEmail, actionCodeSettings } from '../../firebase';
import AuthHeader from '../layout/AuthHeader';

// Password validation function
const validatePassword = (password) => {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSymbols = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  
  if (password.length < minLength) {
    return { valid: false, message: `Password must be at least ${minLength} characters long` };
  }
  
  if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSymbols) {
    return { 
      valid: false, 
      message: 'Password must include uppercase and lowercase letters, numbers, and symbols'
    };
  }
  
  return { valid: true, message: '' };
};

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: '' });
  
  // Terms and conditions state
  const [hasVisitedTerms, setHasVisitedTerms] = useState(false);
  const [hasVisitedPrivacy, setHasVisitedPrivacy] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const navigate = useNavigate();
  const { loginWithGoogle, currentUser, isAnonymous } = useAuth();

  // Redirect if user is already logged in and not anonymous
  useEffect(() => {
    if (currentUser && !isAnonymous) {
      navigate('/');
    }
  }, [currentUser, isAnonymous, navigate]);

  // Check if user has visited the links from localStorage
  useEffect(() => {
    const visitedTerms = localStorage.getItem('visitedTermsFromRegister') === 'true';
    const visitedPrivacy = localStorage.getItem('visitedPrivacyFromRegister') === 'true';
    setHasVisitedTerms(visitedTerms);
    setHasVisitedPrivacy(visitedPrivacy);
  }, []);

  // Check password strength when it changes
  useEffect(() => {
    if (password) {
      const validation = validatePassword(password);
      setPasswordStrength({
        score: validation.valid ? 100 : 0,
        feedback: validation.message
      });
    } else {
      setPasswordStrength({ score: 0, feedback: '' });
    }
  }, [password]);

  // Handle link clicks to track visits
  const handleTermsClick = () => {
    localStorage.setItem('visitedTermsFromRegister', 'true');
    setHasVisitedTerms(true);
    window.open('/terms', '_blank');
  };

  const handlePrivacyClick = () => {
    localStorage.setItem('visitedPrivacyFromRegister', 'true');
    setHasVisitedPrivacy(true);
    window.open('/privacy', '_blank');
  };

  const canCheckTerms = hasVisitedTerms && hasVisitedPrivacy;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailSent(false);

    // Validate input
    if (!email || !password || !confirmPassword) {
      return setError('All fields are required');
    }

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    // Validate password strength
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      return setError(passwordValidation.message);
    }

    // Validate terms agreement
    if (!agreedToTerms) {
      return setError('You must agree to the Terms of Service and Privacy Policy to register');
    }

    if (!canCheckTerms) {
      return setError('You must read the Terms of Service and Privacy Policy before registering');
    }

    setLoading(true);
    
    try {
      // First try to create the user directly - this will immediately 
      // fail with auth/email-already-in-use if the email exists
      try {
        // Attempt to create user - if this fails with email-already-in-use, 
        // we know the email already exists
        await createUserWithEmailAndPassword(auth, email, password);
        
        // If we get here, user was created, which means the email didn't exist
        // Now we need to delete the user because we want email verification first
        if (auth.currentUser) {
          await auth.currentUser.delete();
        }
      } catch (authError) {
        console.error("User creation check error:", authError.code, authError.message);
        
        // If email already exists, show error and stop
        if (authError.code === 'auth/email-already-in-use') {
          setError("This email is already registered. Please use a different email or log in.");
          setLoading(false);
          return;
        }
        
        // For other errors, let the registration continue with email verification
      }
      
      // Store password for later
      localStorage.setItem('pendingUserPassword', password);
      localStorage.setItem('emailForSignIn', email);
      
      // Clear the visit tracking after successful registration
      localStorage.removeItem('visitedTermsFromRegister');
      localStorage.removeItem('visitedPrivacyFromRegister');
      
      // Now send the verification email
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      
      // Set emailSent to true
      setEmailSent(true);
      console.log('Verification email sent successfully to:', email);
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle various error cases
      if (error.code === 'auth/email-already-in-use') {
        setError("This email is already registered. Please use a different email or log in.");
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address format');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(error.message || 'Failed to send verification email. Please try again.');
      }
      
      // Ensure emailSent remains false when there's an error
      setEmailSent(false);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    
    try {
      await loginWithGoogle();
      navigate('/');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Show email sent confirmation if the email was sent successfully
  if (emailSent) {
    return (
      <>
        <AuthHeader />
        <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4 pt-20 transition-colors duration-200 select-none">
          <div className="w-full max-w-md p-6 bg-card border border-border rounded-lg shadow-lg text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h2 className="mt-4 text-2xl font-bold text-foreground">Verification Email Sent</h2>
            <p className="mt-2 text-muted-foreground">
              We've sent a verification link to <strong>{email}</strong>. Please check your email and click the link to complete your registration.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              If you don't see the email, check your spam folder or <button onClick={() => setEmailSent(false)} className="text-primary hover:text-primary/80 hover:underline transition-colors">try again</button> with a different email.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AuthHeader />
      <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4 pt-20 transition-colors duration-200 select-none">
        <h1 className="text-3xl font-bold text-foreground mb-8 select-none">Create your ThatAIHumanizer account</h1>
        
        <div className="w-full max-w-md bg-card border border-border rounded-lg shadow-lg p-6">
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive rounded select-none">
              {error}
            </div>
          )}
          
          {/* Google Login Button */}
          <div className="mb-6">
            <button 
              onClick={handleGoogleLogin}
              disabled={loading}
              type="button"
              className="w-full flex items-center justify-center py-2 px-4 border border-border rounded-full text-foreground bg-card hover:bg-accent disabled:opacity-50 select-none transition-colors"
            >
              <svg className="h-5 w-5 mr-2 select-none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" 
                fill="#4285F4" />
              </svg>
              Continue with Google
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-muted-foreground">Or sign up with email</span>
            </div>
          </div>
          
          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
                placeholder="Email"
                required
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>
            
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
                placeholder="Password"
                required
                autoComplete="new-password"
                autoCorrect="off"
                spellCheck="false"
              />
              <button 
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <svg className="h-5 w-5 text-muted-foreground select-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-muted-foreground select-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            
            {/* Password strength indicator */}
            {password && (
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Password strength</span>
                  <span className={`font-medium ${passwordStrength.score === 100 ? 'text-green-600' : 'text-orange-600'}`}>
                    {passwordStrength.score === 100 ? 'Strong' : 'Weak'}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 mt-1">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength.score === 100 ? 'bg-green-500' : 'bg-orange-500'
                    }`}
                    style={{ width: `${passwordStrength.score}%` }}
                  ></div>
                </div>
                {passwordStrength.feedback && (
                  <p className="text-xs text-muted-foreground mt-1">{passwordStrength.feedback}</p>
                )}
              </div>
            )}
            
            <div>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
                placeholder="Confirm Password"
                required
                autoComplete="new-password"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>
            
            {/* Terms and Conditions Checkbox */}
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Please read the following documents before proceeding:
              </div>
              
              <div className="flex flex-col space-y-2">
                <button
                  type="button"
                  onClick={handleTermsClick}
                  className={`text-sm text-left px-3 py-2 rounded border transition-colors ${
                    hasVisitedTerms 
                      ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100' 
                      : 'border-border text-primary hover:text-primary/80 hover:bg-accent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="underline">📄 Read Terms of Service</span>
                    {hasVisitedTerms && (
                      <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={handlePrivacyClick}
                  className={`text-sm text-left px-3 py-2 rounded border transition-colors ${
                    hasVisitedPrivacy 
                      ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100' 
                      : 'border-border text-primary hover:text-primary/80 hover:bg-accent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="underline">🔒 Read Privacy Policy</span>
                    {hasVisitedPrivacy && (
                      <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    id="agreeTerms"
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    disabled={!canCheckTerms}
                    className={`h-4 w-4 rounded border focus:ring-2 focus:ring-ring ${
                      canCheckTerms 
                        ? 'border-border text-primary focus:ring-ring cursor-pointer' 
                        : 'border-muted bg-muted cursor-not-allowed opacity-50'
                    }`}
                  />
                </div>
                <label 
                  htmlFor="agreeTerms" 
                  className={`text-sm leading-5 ${
                    canCheckTerms 
                      ? 'text-foreground cursor-pointer' 
                      : 'text-muted-foreground cursor-not-allowed'
                  }`}
                >
                  I have read and agree to the Terms of Service and Privacy Policy
                  {!canCheckTerms && (
                    <span className="block text-xs text-destructive mt-1">
                      Please read both documents above before agreeing
                    </span>
                  )}
                </label>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading || passwordStrength.score !== 100 || !agreedToTerms}
              className="w-full py-2 px-4 border border-transparent rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 transition-colors"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
            
            <div className="text-center mt-3 select-none">
              <a href="/login" className="text-sm text-primary hover:text-primary/80 transition-colors">
                Already have an account?
              </a>
            </div>
          </form>
        </div>
      </div>
    </>
  );
} 